/**
 * Tableau de bord (direction C, 2026-11-03, `docs/design/ecarts-tableau-de-bord.md`) : ce qui attend le membre, ce qui
 * est en route, son prochain départ. L'écran affiche ; ces règles décident, et sont testées dans
 * `utils/__tests__/dashboardItems.test.js`. Aucun montant n'est calculé ici : prix, net du transporteur et chiffres de
 * PRO viennent du serveur.
 */

/** Missions engagées et pas encore livrées, de la moins à la plus avancée. */
const PROGRESS = { RESERVED: 1, PAYMENT_PENDING: 1, ACCEPTED: 1, IN_ESCROW: 2, IN_TRANSIT: 3 };
/** Missions payées, comme le résumé du serveur (`missions._PAID_STATUSES`) : ce que l'expéditeur a dépensé. */
export const SPENT_STATUSES = ['IN_ESCROW', 'IN_TRANSIT', 'DELIVERED', 'COMPLETED'];
const OPEN_TRIP = ['ACTIVE', 'PUBLISHED', 'FULL'];
const ENDED_MISSION = ['CANCELLED', 'DECLINED'];

/** Date « AAAA-MM-JJ » du serveur lue en date locale (jamais `new Date('2026-11-08')`, minuit en temps universel). */
export function localDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** « lundi 3 novembre » : date en toutes lettres des phrases d'accueil (décision du 2026-11-03). */
export function longDay(date) {
  return date ? date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
}

/** Jours entiers entre aujourd'hui et `date` (0 = aujourd'hui). */
export function daysUntil(date, now = new Date()) {
  if (!date) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((date - today) / 86400000);
}

/** Échéance d'une proposition : 12 h pour une directe (`response_deadline`), sinon `expires_at`. */
export function deadlineOf(p) {
  return p?.response_deadline || p?.expires_at || null;
}

/** Heures restantes avant l'échéance (jamais négatives). */
export function hoursLeft(p, now = new Date()) {
  const d = deadlineOf(p);
  return d ? Math.max(0, (new Date(d) - now) / 3600000) : null;
}

/**
 * Propositions reçues qui attendent ma réponse, la plus urgente d'abord. `meSide` garde celles de la vue (un compte
 * hybride reçoit des deux côtés) : l'expéditeur reçoit celles des transporteurs, et inversement.
 */
export function pendingReceived(propositions, meSide) {
  const from = meSide === 'sender' ? 'CARRIER_TO_SENDER' : meSide === 'carrier' ? 'SENDER_TO_CARRIER' : null;
  return (propositions || [])
    .filter((p) => p.status === 'PENDING' && (!from || !p.direction || p.direction === from))
    .sort((a, b) => new Date(deadlineOf(a) || 8.64e15) - new Date(deadlineOf(b) || 8.64e15));
}

/** Expéditeur : missions livrées dont il doit confirmer la réception (ce qui déclenche le paiement du transporteur). */
export function toConfirm(missions, uid) {
  return (missions || []).filter((m) => m.status === 'DELIVERED' && m.shipper_id === uid);
}

/** Transporteur : missions livrées qui attendent la confirmation de l'expéditeur. */
export function awaitingConfirmation(missions, uid) {
  return (missions || []).filter((m) => m.status === 'DELIVERED' && m.carrier_id === uid);
}

/** Expéditeur : la mission la plus avancée en cours (« En route ») ; à égalité, la plus récemment mise à jour. */
export function inRoute(missions, uid) {
  return (missions || [])
    .filter((m) => m.shipper_id === uid && PROGRESS[m.status])
    .sort((a, b) => (PROGRESS[b.status] - PROGRESS[a.status]) || (new Date(b.updated_at || 0) - new Date(a.updated_at || 0)))[0] || null;
}

/**
 * Frise d'une mission : acceptée → paiement sécurisé → remis au transporteur → arrivé ; chaque étape avec sa date
 * quand le serveur la donne. `current` = l'étape en cours (la première pas encore faite).
 */
export function missionSteps(m) {
  const level = m?.status === 'DELIVERED' || m?.status === 'COMPLETED' ? 4 : (PROGRESS[m?.status] || 0);
  const carrier = m?.carrier?.first_name;
  const steps = [
    { key: 'accepted', label: m?.payment_reserved || m?.status === 'RESERVED' ? 'Réservé' : 'Accepté', date: m?.created_at, done: level >= 1 },
    { key: 'paid', label: 'Paiement sécurisé', date: m?.payment?.escrow_at || null, done: level >= 2 },
    { key: 'pickup', label: carrier ? `Remis à ${carrier}` : 'Remis au transporteur', date: m?.in_transit_at || null, done: level >= 3 },
    { key: 'arrived', label: 'Arrivé', date: m?.delivered_at || null, done: level >= 4 },
  ];
  const current = steps.findIndex((s) => !s.done);
  return { steps, current: current === -1 ? steps.length : current };
}

/** Transporteur : son prochain trajet ouvert, à partir d'aujourd'hui (le plus proche). */
export function nextTrip(trips, now = new Date()) {
  return (trips || [])
    .filter((t) => OPEN_TRIP.includes(t.status) && daysUntil(localDay(t.departure_date), now) >= 0)
    .sort((a, b) => localDay(a.departure_date) - localDay(b.departure_date))[0] || null;
}

/** Place d'un trajet : réservée (colis de ses missions engagées), libre (`available_weight_kg`), totale. */
export function tripLoad(trip, missions) {
  const reserved = (missions || [])
    .filter((m) => m.trip_id === trip?.id && !ENDED_MISSION.includes(m.status))
    .reduce((kg, m) => kg + Number(m.shipment?.weight_kg || 0), 0);
  const free = Number(trip?.available_weight_kg || 0);
  return { reserved: Math.round(reserved * 10) / 10, free, total: Math.round((reserved + free) * 10) / 10 };
}

const WORDS = ['Rien', 'Une chose', 'Deux choses', 'Trois choses', 'Quatre choses', 'Cinq choses'];

/** Phrase d'accueil de l'expéditeur : « Deux choses vous attendent aujourd'hui, lundi 3 novembre. » */
export function senderTodayLine(count, now = new Date()) {
  if (!count) return `Rien ne vous attend aujourd’hui, ${longDay(now)}.`;
  const what = count < WORDS.length ? WORDS[count] : `${count} choses`;
  const verb = count <= 1 ? 'vous attend' : 'vous attendent';
  return `${what} ${verb} aujourd’hui, ${longDay(now)}.`;
}

/** Phrase d'accueil du transporteur : « Prochain départ dans 13 jours : Paris → Dakar, le dimanche 8 novembre. » */
export function carrierTodayLine(trip, now = new Date()) {
  if (!trip) return 'Aucun départ prévu pour l’instant.';
  const day = localDay(trip.departure_date);
  const n = daysUntil(day, now);
  const when = n === 0 ? 'aujourd’hui' : n === 1 ? 'demain' : `dans ${n} jours`;
  return `Prochain départ ${when} : ${trip.departure_city} → ${trip.arrival_city}, le ${longDay(day)}.`;
}

/** Nom court d'un membre : « Amina F. » */
export function shortName(member) {
  if (!member) return '';
  const last = member.last_name ? ` ${member.last_name[0]}.` : '';
  return `${member.first_name || ''}${last}`.trim();
}
